import {
  Box,
  Button,
  Container,
  Divider,
  Link,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useState } from 'react';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { getFirebaseStatus } from '@/infrastructure/firebase/firebaseStatus.js';
import { DISCLAMER_TEXT } from '@k-ucap/shared';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const signInWithEmail = useAuthStore((s) => s.signInWithEmail);
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const signInAsMock = useAuthStore((s) => s.signInAsMock);
  const sendPasswordReset = useAuthStore((s) => s.sendPasswordReset);
  const isLoading = useAuthStore((s) => s.isLoading);
  const isSigningIn = useAuthStore((s) => s.isSigningIn);
  const error = useAuthStore((s) => s.error);
  const clearError = useAuthStore((s) => s.clearError);

  const status = getFirebaseStatus();
  const isMock = import.meta.env.VITE_FORCE_MOCK_MODE === 'true' || status.mode !== 'FIREBASE';
  const isBusy = isLoading || isSigningIn;

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await signInWithEmail(email, password);
  };

  const handleGoogleLogin = async () => {
    clearError();
    await signInWithGoogle();
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await sendPasswordReset(resetEmail);
    setResetSent(true);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 10 }}>
      <Paper elevation={4} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          K-UCAP
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Korea Unmanned System Conformance Assessment Platform
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
            {error}
          </Alert>
        )}
        {resetSent && (
          <Alert severity="success" sx={{ mb: 2 }}>
            비밀번호 재설정 이메일을 발송했습니다.
          </Alert>
        )}

        {!showReset ? (
          <Box
            component="form"
            onSubmit={handleEmailLogin}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}
          >
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isBusy}
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isBusy}
              required
            />
            <Button variant="contained" type="submit" disabled={isBusy} fullWidth>
              {isSigningIn ? <CircularProgress size={24} /> : '로그인'}
            </Button>

            <Divider sx={{ my: 1 }}>OR</Divider>

            <Button
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={isBusy}
              fullWidth
            >
              {isSigningIn ? <CircularProgress size={24} /> : 'Google로 로그인'}
            </Button>

            <Link component="button" type="button" variant="body2" onClick={() => setShowReset(true)} sx={{ mt: 1 }}>
              비밀번호 재설정
            </Link>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleReset} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              disabled={isBusy}
              required
            />
            <Button variant="contained" type="submit" disabled={isBusy} fullWidth>
              {isSigningIn ? <CircularProgress size={24} /> : '재설정 이메일 발송'}
            </Button>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => {
                setShowReset(false);
                setResetSent(false);
              }}
            >
              로그인 화면으로 돌아가기
            </Link>
          </Box>
        )}

        {isMock && (
          <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button variant="outlined" size="small" onClick={() => signInAsMock('ADMIN')} disabled={isSigningIn}>
              Mock Admin
            </Button>
            <Button variant="outlined" size="small" onClick={() => signInAsMock('VIEWER')} disabled={isSigningIn}>
              Mock Viewer
            </Button>
          </Box>
        )}
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        {DISCLAMER_TEXT}
      </Alert>
    </Container>
  );
};
